import React, { useState, useEffect } from 'react';
import { OrganisationScoredCompaniesDto, FilterItemDto } from 'models/dtoTypes';
import { IFilterField } from 'models/types';
import { styled } from '@mui/material/styles';
import { get } from 'lodash';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Pagination from '@mui/material/Pagination';
import Paper from '@mui/material/Paper';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Link from 'components/Link';
import FilterPanel from 'components/FilterPanel';
import ExportKosmosReportsCsvLink from 'kosmos/components/ExportKosmosReportsCsvLink_Legacy';
import { GetScoredCompanyReports } from 'services/openapiClient';

const PREFIX = 'ResourceTable';
const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    color: 'black',
    fontWeight: 'bold',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 16,
    // borderRadius: 5,
    borderTop: '1px solid',
    borderBottom: '1px solid',
    '&:first-of-type': {
      borderLeft: '1px solid',
      borderTopLeftRadius: 5,
      borderBottomLeftRadius: 5,
    },
    '&:last-child': {
      borderRight: '1px solid',
      borderTopRightRadius: 5,
      borderBottomRightRadius: 5,
      // transform: 'scale(1.05)',
    },
  },
}));

const classes = {
  root: `${PREFIX}-root`,
  actions: `${PREFIX}-actions`,
  pagination: `${PREFIX}-pagination`,
  tableActions: `${PREFIX}-tableActions`,
};

const Root = styled('div')(() => ({
  position: 'relative',
  width: '100%',

  [`& .${classes.tableActions}`]: {
    display: 'flex',
    margin: 10,

    [`& .${classes.pagination}`]: {
      paddingTop: 10,
      paddingBottom: 10,
      flex: 1,
    },

    [`& .${classes.actions}`]: {
      paddingTop: 10,
      paddingBottom: 10,
    },
  },
}));

export type IActionFn = (
  model: OrganisationScoredCompaniesDto,
) => void | Promise<void | OrganisationScoredCompaniesDto | undefined>;

interface ITableRowAction {
  label: string;
  onClick: IActionFn;
  hide?: (model: OrganisationScoredCompaniesDto) => boolean;
}

interface IColumn {
  label: string;
  dataIndex?: string;
  sortIndex?: string;
  actions?: ITableRowAction[];
  render?: (model: OrganisationScoredCompaniesDto) => JSX.Element | string;
}

type ResourceWithCount<OrganisationScoredCompaniesDto> = {
  pageCount?: number;
  count?: number;
  page?: number;
  items?: OrganisationScoredCompaniesDto[] | null;
};

interface IProps {
  filterSchema?: IFilterField[];
  columns: IColumn[];
  onFetch: (options: {
    pageIndex: number;
    organisationId?: string;
  }) => Promise<ResourceWithCount<OrganisationScoredCompaniesDto>>;
  defaultQuery?: FilterItemDto[];
  defaultSort?: { sortColumn: string; isDesc: boolean };
}

interface IFilters {
  [key: string]: string;
}

function SingleItem(props: { row: OrganisationScoredCompaniesDto; columns: IColumn[] }) {
  const { row, columns } = props;
  const [model, setModel] = useState<OrganisationScoredCompaniesDto>(row);
  const [loading, setLoading] = useState(false);

  const renderCell = (column: IColumn) => {
    const { dataIndex, actions, render } = column;
    const processAction = async (fn: IActionFn) => {
      setLoading(true);
      const newModel = await fn(model);
      if (newModel) {
        setModel(newModel);
      }
      setLoading(false);
    };
    if (actions) {
      // render action list
      if (loading) {
        return <CircularProgress size={20} />;
      }
      return (
        <Stack direction="row" spacing={1}>
          {actions
            .filter((a) => !(a.hide && a.hide(model)))
            .map((action) => {
              const { label, onClick } = action;
              return (
                <Link key={label} isAsync onClick={() => processAction(onClick)} text={label} />
              );
            })}
        </Stack>
      );
    } else if (render) {
      // render customised cell
      return render(model);
    } else if (dataIndex) {
      return get(model, dataIndex || '');
    } else {
      return '';
    }
  };

  return (
    <TableRow>
      {columns.map((c, index) => (
        <TableCell key={index}>{renderCell(c)}</TableCell>
      ))}
    </TableRow>
  );
}

export default function ScoredCompaniesResourceTable(props: IProps) {
  const { columns, onFetch, defaultQuery = [], filterSchema, defaultSort } = props;
  const [filters, setFilters] = useState<IFilters>({});
  const [filtered, setFiltered] = useState(false);
  const [sort, setSort] = useState<{ sortColumn: string; isDesc: boolean } | undefined>(
    defaultSort,
  );

  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ResourceWithCount<OrganisationScoredCompaniesDto>>();

  const loadResources = async (pageIndex: number, reset = false) => {
    setLoading(true);
    const filterItems = reset
      ? []
      : Object.keys(filters).map((k) => ({ column: k, value: filters[k] }));
    const options = {
      filters: [...defaultQuery, ...filterItems],
      pageIndex,
      ...(sort || {}),
    };
    try {
      const data = await onFetch(options);
      setResult(data);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => {
    void loadResources(page);
  }, [page, sort]);

  const rows = result?.items || [];

  // Avoid a layout jump when reaching the last page with empty rows.
  const handleChangePage = (event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeFilter = (value: string, name: string) => {
    setFilters({
      ...filters,
      [name]: value,
    });
  };

  const handleFilter = async () => {
    setLoading(true);

    const hasFilters = Object.values(filters).find((v) => v !== undefined && v !== null);
    if (hasFilters) {
      await loadResources(0);
      setFiltered(true);
    }
    setLoading(false);
  };

  const handleReset = async () => {
    setLoading(true);
    if (filtered) {
      setFilters({});
      setFiltered(false);
      await loadResources(0, true);
    }
    setLoading(false);
  };

  const handleSort = (sortColumn: string) => {
    const previousSortIndex = sort?.sortColumn;
    if (previousSortIndex === sortColumn) {
      setSort({
        sortColumn,
        isDesc: !sort?.isDesc,
      });
    } else {
      setSort({
        sortColumn,
        isDesc: false,
      });
    }
  };

  const renderLoadingRows = () => {
    return (
      <>
        <LinearProgress />
      </>
    );
  };

  const renderTableHead = () => {
    const { sortColumn, isDesc } = sort || {};
    return (
      <TableHead>
        <TableRow>
          {columns.map((c, index) => {
            const { label, sortIndex } = c;
            if (sortIndex) {
              return (
                <StyledTableCell key={index}>
                  <TableSortLabel
                    active={sortColumn === sortIndex}
                    direction={isDesc ? 'desc' : 'asc'}
                    onClick={() => handleSort(sortIndex)}
                  >
                    {label}
                  </TableSortLabel>
                </StyledTableCell>
              );
            }
            return <StyledTableCell key={index}>{label}</StyledTableCell>;
          })}
        </TableRow>
      </TableHead>
    );
  };

  const handleDownloadCsvReports = async () => {
    const filterItems = Object.keys(filters).map((k) => ({
      column: k,
      value: filters[k],
    }));
    const options = {
      filters: [...defaultQuery, ...filterItems],
      ...(sort || {}),
    };

    return await GetScoredCompanyReports(options);
  };

  return (
    <Root>
      {filterSchema && (
        <FilterPanel
          fields={filterSchema}
          filtered={filtered}
          filters={filters}
          onChangeFilter={handleChangeFilter}
          onSearch={handleFilter}
          onReset={handleReset}
        />
      )}
      <TableContainer component={Paper}>
        {loading && renderLoadingRows()}

        {!loading && (
          <Table sx={{ minWidth: 500 }} aria-label="custom pagination table">
            {renderTableHead()}
            <TableBody>
              {rows.map((row, index) => (
                <SingleItem key={index} row={row} columns={columns} />
              ))}
            </TableBody>
          </Table>
        )}
        {result?.items?.length ? (
          <div className={classes.tableActions}>
            <div className={classes.pagination}>
              <Pagination count={result.pageCount} page={page} onChange={handleChangePage} />
            </div>
            <div className={classes.actions}>
              <ExportKosmosReportsCsvLink
                onDownloadReports={handleDownloadCsvReports}
                filters={Object.keys(filters).map((key) => ({
                  label: filterSchema?.find((item) => item.name === key)?.label || '',
                  name: key,
                  value: filters[key],
                }))}
                fileName="scored-companies.csv"
              />
            </div>
          </div>
        ) : null}
      </TableContainer>
    </Root>
  );
}
